import React, { useCallback, useRef, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import { useQueryClient } from 'react-query'
import SvgIcon from '@mui/material/SvgIcon'

import { ReactComponent as ActivityIcon } from '../../../../../assets/images/activity_icon.svg'
import { ReactComponent as EvidenceIcon } from '../../../../../assets/images/evidence_icon.svg'
import { ReactComponent as ValueIcon } from '../../../../../assets/images/valuemessage_icon.svg'
import { ReactComponent as CommentIcon } from '../../../../../assets/images/comment_icon.svg'

import { useGetLinkedItemsQuery } from '../../../../../shared/queries/linking/useGetLinkedItemsQuery'
import type { LinkedItems, LinkingItem } from '../../../../../components/Linking/Linking.types'
import TPPLinking from '../../../../../components/Linking/TPPLinking'
import CellTooltip from './CellTooltip'

import { isCellLocked } from '../utils/CellLockUtil'
import { TPPCellCommentsPopover } from '../../../../../shared/components/Comments/variants/PopoverComments/TPPCellCommentsPopover'

type LinkTabType = 'evidence' | 'activity' | 'valueMessage' | null

export default function CellLinkingMenu({ cell, isColumnLocked = false, isColumnArchived = false, canEdit = true }) {
    const queryClient = useQueryClient()
    const locked = isCellLocked(cell?.locked) || isColumnLocked || isColumnArchived || !canEdit
    const [linkingTab, setLinkingTab] = React.useState<LinkTabType>(null)
    const [isMenuHovered, setIsMenuHovered] = React.useState(false)
    const [commentsOpen, setCommentsOpen] = useState(false)
    const [commentsAnchorEl, setCommentsAnchorEl] = useState<HTMLElement | null>(null)

    const { data: linkedItems, isLoading: isLinkedItemsLoading } = useGetLinkedItemsQuery(
        isMenuHovered ? cell?._id : undefined,
        'tppCell'
    ) as { data?: LinkedItems; isLoading: boolean }

    const linkedEvidenceItems: LinkingItem[] = linkedItems?.evidence ?? []
    const linkedActivityItems: LinkingItem[] = linkedItems?.activity ?? []
    const linkedValueMessageItems: LinkingItem[] = linkedItems?.valueMessage ?? []
    const hasComments: Boolean = cell?.hasComment ?? false

    const actionsButtonRef = useRef<HTMLButtonElement | null>(null)

    const handleOpenComments = () => {
        const btn = actionsButtonRef.current
        if (!btn) return

        setCommentsAnchorEl(btn)
        setCommentsOpen((prev) => !prev)
    }

    const handleCloseComments = () => {
        setCommentsOpen(false)
        setCommentsAnchorEl(null)
    }

    const handleCommentPosted = useCallback(() => {
        queryClient.setQueryData(['tppCells', cell.tppId], (old: any[]) => {
            if (!old) return []
            return old.map((c: any) => (c._id === cell._id ? { ...c, hasComment: true } : c))
        })
    }, [queryClient, cell?._id, cell?.tppId])

    const handleCommentRemoved = useCallback(() => {
        const comments = queryClient.getQueryData(['comments', String(cell?._id), 'tppCell'])
        const stillHasComments = Array.isArray(comments) && comments.length > 0

        if (!stillHasComments) {
            handleCloseComments()
        }

        queryClient.setQueryData(['tppCells', cell.tppId], (old: any[]) => {
            if (!old) return []
            return old.map((c: any) => (c._id === cell._id ? { ...c, hasComment: stillHasComments } : c))
        })
    }, [queryClient, cell?._id, cell?.tppId])

    const handleLinkingClose = useCallback(() => {
        setLinkingTab(null)

        const freshLinkedItems = queryClient.getQueryData(['linkedItems', cell?._id, 'tppCell']) as any
        if (!freshLinkedItems) return

        queryClient.setQueryData(['tppCells', cell.tppId], (old: any[]) => {
            if (!old) return []
            return old.map((c: any) =>
                c._id === cell._id
                    ? {
                          ...c,
                          linkedEvidence: (freshLinkedItems.evidence || []).map((e: any) => e._id),
                          linkedActivities: (freshLinkedItems.activity || []).map((a: any) => a._id),
                          linkedValueMessages: (freshLinkedItems.valueMessage || []).map((v: any) => v._id),
                      }
                    : c
            )
        })
    }, [queryClient, cell?._id, cell?.tppId])

    const handleLinkingClick = (tab: LinkTabType) => {
        if (locked) return
        setLinkingTab(tab)
    }

    const hasEvidenceLinks = Array.isArray(cell?.linkedEvidence) && cell.linkedEvidence.length > 0
    const hasActivityLinks = Array.isArray(cell?.linkedActivities) && cell.linkedActivities.length > 0
    const hasValueMessageLinks = Array.isArray(cell?.linkedValueMessages) && cell.linkedValueMessages.length > 0

    return (
        <Box
            sx={{ display: 'flex', alignItems: 'center' }}
            onMouseEnter={() => setIsMenuHovered(true)}
            onMouseLeave={() => setIsMenuHovered(false)}
        >
            <CellTooltip
                type="evidence"
                items={linkedEvidenceItems}
                emptyLabel="Link Evidence"
                isLoading={hasEvidenceLinks && isLinkedItemsLoading}
            >
                <span>
                    <IconButton
                        disabled={false}
                        onClick={() => handleLinkingClick('evidence')}
                        sx={{
                            color: hasEvidenceLinks ? 'primary.main' : 'figma.grayscale.tier2',
                        }}
                    >
                        <SvgIcon component={EvidenceIcon} inheritViewBox fontSize="inherit" />
                    </IconButton>
                </span>
            </CellTooltip>

            <CellTooltip
                type="activity"
                items={linkedActivityItems}
                emptyLabel="Link Activity"
                isLoading={hasActivityLinks && isLinkedItemsLoading}
            >
                <span>
                    <IconButton
                        disabled={false}
                        onClick={() => handleLinkingClick('activity')}
                        sx={{
                            color: hasActivityLinks ? 'primary.main' : 'figma.grayscale.tier2',
                        }}
                    >
                        <SvgIcon component={ActivityIcon} inheritViewBox fontSize="inherit" />
                    </IconButton>
                </span>
            </CellTooltip>

            <CellTooltip
                type="valueMessage"
                items={linkedValueMessageItems}
                emptyLabel="Link Value Message"
                isLoading={hasValueMessageLinks && isLinkedItemsLoading}
            >
                <span>
                    <IconButton
                        disabled={false}
                        onClick={() => handleLinkingClick('valueMessage')}
                        sx={{
                            color: hasValueMessageLinks ? 'primary.main' : 'figma.grayscale.tier2',
                        }}
                    >
                        <SvgIcon component={ValueIcon} inheritViewBox fontSize="inherit" />
                    </IconButton>
                </span>
            </CellTooltip>
            {hasComments && (
                <CellTooltip type="comment" items={[]} emptyLabel="Comments">
                    <span>
                        <IconButton
                            ref={actionsButtonRef}
                            disabled={false}
                            onClick={() => handleOpenComments()}
                            sx={{
                                color: 'primary.main',
                            }}
                        >
                            <SvgIcon component={CommentIcon} inheritViewBox fontSize="inherit" />
                        </IconButton>
                    </span>
                </CellTooltip>
            )}
            {commentsOpen && (
                <TPPCellCommentsPopover
                    open={commentsOpen}
                    anchorEl={commentsAnchorEl}
                    onClose={handleCloseComments}
                    dataId={String(cell?._id)}
                    dataType={'tppCell'}
                    onCommentPosted={handleCommentPosted}
                    onCommentRemoved={handleCommentRemoved}
                />
            )}
            {linkingTab && (
                <TPPLinking
                    type="tppCell"
                    id={cell?._id}
                    previousDataStack={[]}
                    initialTab={linkingTab}
                    onClose={handleLinkingClose}
                />
            )}
        </Box>
    )
}
